
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import DataTable from "@/components/admin/DataTable";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

interface ProfileOverviewUsageStat {
  user_id: string;
  user_email: string;
  profile_count: number;
  last_generated: string;
}

const ProfileOverviewUsageAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ProfileOverviewUsageStat[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Call the database function to get profile overview usage stats
        const { data, error } = await supabase.rpc('get_profile_overview_usage_stats');
        
        if (error) {
          throw error;
        }

        setStats(data || []);
        
        // Calculate total profiles generated
        const total = data?.reduce((sum: number, item: ProfileOverviewUsageStat) => sum + item.profile_count, 0) || 0;
        setTotalCount(total);
      } catch (err) {
        console.error("Error fetching profile overview usage stats:", err);
        setError("Failed to load profile overview usage statistics");
        toast({
          title: "Error",
          description: "Failed to load profile overview usage data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [toast]);

  const exportCsv = () => {
    if (!stats.length) return;

    // Format CSV header
    const headers = Object.keys(stats[0]).join(",");
    // Format CSV rows
    const rows = stats.map(row => 
      Object.values(row).map(value => 
        typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
      ).join(",")
    ).join("\n");
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "profile_overview_usage.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      key: "user_email",
      label: "User Email"
    },
    {
      key: "profile_count",
      label: "Profiles Generated",
      format: (value: number) => (
        <Badge variant="outline" className="font-mono">
          {value}
        </Badge>
      )
    },
    {
      key: "last_generated",
      label: "Last Generated",
      format: (value: string) => format(new Date(value), 'MMM d, yyyy h:mm a')
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile Overview Generation Statistics</CardTitle>
          <CardDescription>
            Track how many profile overviews users have generated with the AI tool
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base font-medium">Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats.length}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base font-medium">Total Profiles</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{totalCount}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-base font-medium">Avg. Per User</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">
                  {stats.length ? (totalCount / stats.length).toFixed(1) : '0'}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportCsv}
              disabled={!stats.length}
              className="flex items-center gap-1"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>

          <DataTable
            data={stats}
            columns={columns}
            idField="user_id"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileOverviewUsageAdmin;
